// src/transport/registry.ts
var GRAPHQL_MASTER_FLAG = "graphql.enabled";
function flagForOperation(op) {
  return `graphql.${op}`;
}
var TRANSPORT_CAPABILITY = {
  /** Route READS to the GraphQL fast path at all. */
  FAST_PATH: "transport.fastPath",
  /** Route IDEMPOTENT WRITES to GraphQL (strictly above {@link FAST_PATH}). */
  WRITES: "transport.writes",
  /**
   * Choose transport PER OPERATION rather than only via the master switch.
   *
   * Read by the settings UI, not by {@link explainTransport} — withholding it
   * hides the per-operation rows, it does not change where an operation routes.
   */
  PER_OPERATION: "transport.perOperation"
};
var TRANSPORT_REGISTRY = {
  "catalog.list": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // The by-id point lookup. One row per alias, where the REST route reads many
  // ids in a single call — so the win is the SINGLE id (a catalog row opening
  // its own page) and the caller caps the rest. Unproven, so REST by default.
  "catalog.item": { default: "rest", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // The bounded "what is related to this?" walk. GraphQL-ONLY: private-api's
  // resolver is the only thing that can reach every table it touches, so REST
  // here means the caller renders the flat reverse-assignment list instead.
  // Registered anyway — an unregistered read is one the master kill-switch
  // cannot reach, which is exactly what it was.
  "catalog.related": {
    default: "graphql",
    graphqlOnly: true,
    capability: TRANSPORT_CAPABILITY.FAST_PATH
  },
  // The address LIST, as opposed to the per-address enrichment reads below.
  // Listed so the read the address tables are built on is switchable like every
  // other one, rather than being the single address path hardwired to REST.
  "address.list": { default: "rest", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // The cross-chain LENS — a different query from `addresses` (which has no
  // `cross_chain` scope member), resolving every chain a string exists on plus
  // the activity rollup in one call. Unproven, so REST by default.
  "address.crossChain": { default: "rest", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.overlays": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.detail": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "transactions.recentFeed": { default: "rest", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "transactions.header": { default: "rest", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.assignments.list": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.entityMapping.read": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.memberships.read": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // "Index a transaction you have never seen." Listed as a write because it can
  // spend a provider call, and eligible because the resolver is IDEMPOTENT by
  // contract: an already-indexed hash comes back as-is with no second call, so a
  // retry after a fallback converges. Unproven, so REST by default.
  "transactions.request": { default: "rest", capability: TRANSPORT_CAPABILITY.WRITES },
  "address.assignments.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES },
  "address.primary.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES },
  "address.entityMapping.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES },
  // The batch membership write is listed because it is idempotent PER MEMBER (each
  // upserts on its own deterministic (entity, validFrom) key), so a retry after a
  // partial write converges instead of double-mapping the members that landed.
  "address.memberships.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES }
};
var DATA_OPERATIONS = Object.keys(TRANSPORT_REGISTRY);
function isDataOperation(value) {
  return typeof value === "string" && Object.hasOwn(TRANSPORT_REGISTRY, value);
}
function capabilityGranted(capability, source) {
  if (capability === void 0) return true;
  if (source === void 0 || source === true) return true;
  if (typeof source === "function") return source(capability);
  return source.has(capability);
}
function flagValue(flags, name) {
  if (flags.valueOf) return flags.valueOf(name);
  return flags.isEnabled(name) ? true : void 0;
}
function explainTransport(op, flags, options = {}) {
  if (options.graphqlConfigured === false) {
    return { transport: "rest", reason: "unconfigured", locked: true };
  }
  const entry = TRANSPORT_REGISTRY[op];
  if (!capabilityGranted(entry?.capability, options.capabilities)) {
    return { transport: "rest", reason: "not-entitled", locked: true };
  }
  if (!flags.isEnabled(GRAPHQL_MASTER_FLAG)) {
    return { transport: "rest", reason: "master-off", locked: false };
  }
  const perOp = flagValue(flags, flagForOperation(op));
  if (perOp !== void 0) {
    return { transport: perOp ? "graphql" : "rest", reason: "operation-flag", locked: false };
  }
  return { transport: entry.default, reason: "registry-default", locked: false };
}
function resolveTransport(op, flags, options = {}) {
  return explainTransport(op, flags, options).transport;
}
function usesGraphql(op, flags, options) {
  return resolveTransport(op, flags, options) === "graphql";
}

export { DATA_OPERATIONS, GRAPHQL_MASTER_FLAG, TRANSPORT_CAPABILITY, TRANSPORT_REGISTRY, explainTransport, flagForOperation, isDataOperation, resolveTransport, usesGraphql };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map